import { Component } from '@angular/core';
import { OfferModalAction } from '../../../../../shared/directives/offer-modal-action/offer-modal-action';
import { Cancel, CurrencyFormatService, WithdrawOffer } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { OfferModalsHeaderComponent } from 'src/shared/ui/comment/modal-actions/offer-header/offer-modals-header.component';

import { OfferHistoryComponent } from '../../offer-history/offer-history.component';
import { ModalCloseComponent } from 'src/shared/ui/comment/modal-actions/modal-close/modal-close.component';
import { ActionSubmittingComponent } from 'src/shared/ui/comment/modal-actions/action-submitting/action-submitting.component';
import { ActionSubmittedComponent } from 'src/search/components/modal-actions/action-submitted/action-submitted.component';
import { DripFeeSource, DripPricingService } from 'src/shared/services/drip-pricing/public-api';
import { FeeToolTipService } from 'src/shared/services/feetooltip/feetooltip.service';
import {
  ModalPriceBreakdownComponent,
  modalTotalDueOf,
  showsTransportationFee
} from 'src/shared/components/modal-price-breakdown/modal-price-breakdown.component';
import {
  ModalPriceBreakdownPattern,
  ModalPriceBreakdownRow,
  ModalPriceBreakdownTranslations
} from 'src/shared/components/modal-price-breakdown/modal-price-breakdown.types';

@Component({
    selector: 'app-withdraw-offer',
    templateUrl: './withdraw-offer.component.html',
    styleUrls: ['./withdraw-offer.component.scss'],
    imports: [
        TranslocoDirective,
        OfferModalsHeaderComponent,
        OfferHistoryComponent,
        ModalCloseComponent,
        ActionSubmittingComponent,
        ActionSubmittedComponent,
        ModalPriceBreakdownComponent
    ]
})
export class WithdrawOfferComponent extends OfferModalAction {
  constructor(
    private readonly _tooltipService: FeeToolTipService,
    private readonly dripPricingService: DripPricingService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
  }

  get vehicleTitle() {
    const { modelYear, make, model, trim, vin } = this.item;
    return `${modelYear} ${make} ${model} ${trim} VIN ${vin}`;
  }

  withdraw() {
    this.event.emit(new WithdrawOffer(this.offerHistory[0].offer.offerUuid, 'WITHDRAW'));
  }

  cancel() {
    this.event.emit(new Cancel());
  }

  // ---------------------------------------------------------------------------
  // All-in pricing (drip pricing, Canada's Competition Act) -- M4.8, copy revised in M4.14
  //
  // This is the Offer Sent state of View Offer: the buyer has made an offer and is waiting on the
  // seller. It was missed by M4.5 for the same reason M4.6 was -- M4 was scoped from the offer states
  // in the design, and the five View Offer states do not all live in counter-offer. This one is its
  // own component, and it quotes a binding figure: msgDescription says the buyer "will be required to
  // purchase the vehicle at this price", and the EHI override adds "plus any applicable fees which may
  // include optional Transportation Fees" -- the exact misstatement the epic exists to remove.
  //
  // Pattern B (ADDITIVE), not Pattern A, and this is the one place the comp was not followed:
  //
  //   The comp (Figma 10493:16426) DECOMPOSES $53,000 into a $52,600 vehicle price plus a $400 service
  //   fee, reaching $53,350 -- i.e. it treats the figure the buyer submitted as already all-in. The
  //   Accept comp (10503:24898) takes the same $53,000 and ADDS the fees, reaching $53,750. Same
  //   figure, two totals, $400 apart; the two comps cannot both be right.
  //
  //   We submit the bare amount: MakeOffer.offerAmount is what the buyer typed, and the backend applies
  //   the fees it returned in buyFee on top of it (see the money criterion in the MR description).
  //   Decomposing here would tell the buyer their $53,000 offer includes a fee the backend is about to
  //   add again, understating what they will pay by exactly the required fees. So the submitted offer is
  //   the additive base and the fees go beneath it. **The comp contradiction is raised with the story
  //   owner rather than resolved silently** -- if the decomposed reading is the intended one, the change
  //   is this pattern constant plus the submit path, not just this pattern constant.
  //
  // Unlike Make Offer there is no control to project: the offer is already submitted and the figure is
  // fixed, so baseAmount is bound directly and useProjectedAmount stays false.
  // ---------------------------------------------------------------------------

  /**
   * Shared with counter-offer's breakdown prefix on purpose.
   *
   * Both are the View Offer modal, they are never on screen together -- the offer status routes to one
   * component or the other -- and every other id in this template is already vom_*. One prefix means
   * automation and analytics target one set of ids for "the all-in total on View Offer" regardless of
   * which state the offer is in. Same reasoning as M4.6 reusing mom_price across the two Make Offer
   * variants, and the opposite of M4.5's two prefixes, which exist because those two breakdowns render
   * at the same time.
   */
  readonly dripIdPrefix = 'vom_price';

  /** Pattern B: the submitted offer, with the required fees added beneath it. */
  readonly additivePattern = ModalPriceBreakdownPattern.ADDITIVE;

  /** The listing's fee schedule, forwarded by OfferModalsComponent since M4.4. No new host plumbing. */
  private get dripFeeSource(): DripFeeSource {
    return this.buyFee;
  }

  /**
   * The offer chain this modal acts on.
   *
   * Reads offerHistory[0] exactly as withdraw() does, so the amount in the breakdown belongs to the
   * offer the button withdraws. Optional chaining rather than the index alone: the chain arrives
   * asynchronously and showDripBreakdown has to answer before it does.
   */
  get rootOffer() {
    return this.offerHistory?.length ? this.offerHistory[0]?.offer : undefined;
  }

  /** The amount the buyer submitted. The breakdown adds the fees to it; it is not already all-in. */
  get dripOfferBaseAmount(): number | undefined {
    return this.rootOffer?.amount;
  }

  /**
   * Read only by showDripBreakdown, as a validity check on the fee source and the amount together.
   *
   * Not bound to anything: allInPriceOf returns the bare amount when there is no fee schedule, so a
   * defined value here is the only way to tell "fees applied" from "no fees to apply" -- and the
   * latter, labelled TOTAL PURCHASE PRICE, is a bare offer presented as an all-in figure.
   */
  get dripOfferAllInAmount(): number | undefined {
    return this.dripPricingService.allInPriceOf(this.dripOfferBaseAmount, this.dripFeeSource);
  }

  /**
   * Gated on the flag, the fee source AND a finite amount -- unlike the Make Offer modals, which
   * deliberately render before the buyer has typed anything. Here the amount is already submitted, so
   * a missing one means the offer chain has not arrived and there is nothing to itemise yet.
   *
   * The fee source is tested with hasFeePayload rather than `!!this.buyFee`. Truthiness is not the same
   * question: `{}` and `{ fees: null }` are both truthy and both carry no fees, and the amount check
   * below cannot catch that -- allInPriceOf returns the bare amount, which is DEFINED. hasFeePayload
   * asks whether a fee COLLECTION arrived, which is what makes the total meaningful.
   *
   * Not requiredFeesOf(...).length either, and the service docblock says so explicitly: a payload that
   * arrived carrying no required fee must still render, with the all-in total legitimately equal to the
   * bare amount. Gating on the row count would withhold the breakdown from a correct zero-fee answer.
   */
  get showDripBreakdown(): boolean {
    return this.dripPricingService.isDripPricingEnabled
      && this.dripPricingService.hasFeePayload(this.dripFeeSource)
      && this.dripOfferAllInAmount !== undefined;
  }

  get dripTransportationFee(): number | undefined {
    return this.dripPricingService.transportationFeeOf(this.dripFeeSource);
  }

  /**
   * Gates the all-in transportation paragraph. Same exported helper the rows use, so the paragraph and
   * the row cannot disagree.
   *
   * The paragraph exists because the binding sentence quotes a total that includes transportation, so
   * the buyer has to be told that part is avoidable. The quoted fee is the gate; see the template for
   * why the all-in path is the only one that renders it.
   */
  get showTransportationDisclosure(): boolean {
    return showsTransportationFee(this.dripTransportationFee);
  }

  /**
   * The binding-offer paragraph.
   *
   * msgDescription's "at this price" -- and, in the EHI override, "plus any applicable fees which may
   * include optional Transportation Fees" -- stops being true once the total on screen already includes
   * them, so the all-in variant names the total instead.
   *
   * CORRECTION. These keys were originally added empty in the base bundles and filled only by
   * ehi/ehi_ca, on the reasoning that base copy would make every tenant render legal-adjacent text they
   * had not reviewed. That trade was the wrong way round: Transloco prints the KEY PATH for an empty
   * value, so a tenant without an override did not fall back to the non-all-in wording -- it showed the
   * buyer the literal string `vehicles.withDrawOffer.msgDescriptionAllIn` where the binding-offer
   * disclosure belongs. The base bundles now carry the same copy the two EHI tenants reviewed, so an
   * unreviewed tenant gets accurate text instead of a key name. A tenant that wants different wording
   * still overrides it.
   *
   * The EHI msgDescription also carries the optional-transportation sentence inside the same string,
   * after a </br></br>. The all-in variant does not: M4.8 split it out into msgDescriptionTransportation,
   * its own paragraph behind a transportation-fee gate, because the all-in total this sentence quotes
   * includes transportation and the buyer has to be told that part is avoidable.
   *
   * This split is why that paragraph's @if also tests showDripBreakdown, as a de-duplication guard: with
   * the flag off the sentence is ALREADY on screen inside msgDescription, so rendering the paragraph as
   * well would state the same disclosure twice. msgDescription itself remains untouched in both
   * directions.
   */
  get descriptionKey(): string {
    return this.showDripBreakdown
      ? 'vehicles.withDrawOffer.msgDescriptionAllIn'
      : 'vehicles.withDrawOffer.msgDescription';
  }

  /**
   * The offer as a modal total: the required fees AND the optional transportation on top of the amount
   * the buyer submitted. Not a second calculation of it -- the same exported helper the breakdown's own
   * total row uses, so the sentence and the figure above it cannot disagree by construction.
   */
  get dripOfferTotalDue(): number | undefined {
    return modalTotalDueOf(this.dripOfferBaseAmount, this.dripFeeRows, this.dripTransportationFee);
  }

  /**
   * The figure the all-in paragraph quotes, transportation INCLUDED.
   *
   * This reverses M4.8's note here, which quoted the all-in price and deferred the comp's inclusive
   * figure to the story owner on the reasoning that transportation is separately elected and avoidable,
   * so an inclusive figure under a binding-offer sentence overstates the obligation. The comp settles it
   * the other way, and the matched Buy Now pair (10360:9184 with transportation, 10360:9217 without)
   * shows that is a rule and not one frame's oversight: the quoted number moves with the total between
   * the two frames rather than staying on the all-in subtotal. Quoting anything other than the figure in
   * the row directly above would put a second, unexplained number in the same panel, which is the defect
   * this epic exists to remove -- and the sentence beneath still tells the buyer transportation is
   * optional whenever a transportation fee is part of that total.
   *
   * dripOfferAllInAmount is deliberately NOT repointed: it is showDripBreakdown's validity check on the
   * fee source and the amount together, never a displayed figure. See its own comment.
   *
   * withCents: false per the comp (Figma 11024:11203), which quotes $53,750 in this sentence and the
   * same whole-dollar figures in the breakdown above it. It tracks Buy Now's equivalent, which moved
   * the same way for the same reason -- the two must agree, because a buyer reading "$53,750" in one
   * modal and "$53,750.00" in the other has to work out whether the obligations differ.
   *
   * This is NOT rounding: formatCurrency does a literal `.replace('.00', '')`, so a genuinely
   * fractional total still renders its cents here rather than being quietly truncated on a binding
   * screen. Only the trailing-zero case is suppressed, and the [showCents]="false" on the breakdown
   * beneath does the same -- this argument and that binding must always move together.
   *
   * Reached only through msgDescriptionAllIn, which descriptionKey selects only when
   * showDripBreakdown is true, so the kill switch takes this whole line out: msgDescription carries no
   * {{ amount }} placeholder at all. No placeholder fallback either, unlike the Make Offer modals:
   * showDripBreakdown already requires a finite submitted amount, so there is no state where this
   * renders without one.
   */
  get quotedPriceDisplay(): string {
    return this.currencyFormatService.formatCurrency(this.dripOfferTotalDue, false);
  }

  /**
   * The fee rows, cached by content.
   *
   * The cache is not an optimisation: translatedRequiredFeesOf maps a fresh array per call, and a
   * template binding is re-evaluated every change-detection pass, so returning it directly hands the
   * OnPush child a new identity each pass and trips NG0100 in development builds.
   */
  get dripFeeRows(): ModalPriceBreakdownRow[] {
    const rows = this._tooltipService.translatedRequiredFeesOf(this.dripFeeSource);
    const key = JSON.stringify(rows);
    if (key !== this.dripFeeRowsKey) {
      this.dripFeeRowsKey = key;
      this.dripFeeRowsCache = rows;
    }
    return this.dripFeeRowsCache;
  }

  /**
   * The breakdown's strings, cached by content for the same reason as dripFeeRows.
   *
   * labelBaseAmount is labelOffer ('OFFER'), not labelOfferAmount ('OFFER AMOUNT').
   *
   * M4.14 bound labelOfferAmount here and raised the one-word difference for sign-off rather than
   * adding a near-duplicate key. Sign-off came back on the comp's side (Figma 11024:11219), so the
   * key now exists. It is a SEPARATE key and not a new value for labelOfferAmount because that one
   * is shared with Make Offer, Make Offer (refactor) and Follow-Up Offer, whose comps still read
   * 'OFFER AMOUNT' -- editing the shared value would have relabelled three breakdowns nobody asked
   * about. Same reasoning as leaving ModalPriceBreakdownComponent's showCents default alone.
   *
   * labelTotalDue is labelModalTotalDue, never labelTotalPurchasePrice: this total INCLUDES
   * transportation while the list-surface total excludes it.
   */
  get dripBreakdownTranslations(): ModalPriceBreakdownTranslations {
    const drip = this._tooltipService.dripTranslations;
    const translations: ModalPriceBreakdownTranslations = {
      labelBaseAmount: drip.labelOffer,
      labelVehiclePrice: drip.labelVehiclePrice,
      labelTransportationFee: this._tooltipService.feeTranslations.TRANSPORTATION_FEE,
      labelTotalDue: drip.labelModalTotalDue,
      textTotalPlaceholder: drip.textTotalPlaceholder,
      ariaLabelPriceComponents: drip.ariaLabelPriceComponents
    };
    const key = JSON.stringify(translations);
    if (key !== this.dripTranslationsKey) {
      this.dripTranslationsKey = key;
      this.dripTranslationsCache = translations;
    }
    return this.dripTranslationsCache;
  }

  private dripFeeRowsKey: string;
  private dripFeeRowsCache: ModalPriceBreakdownRow[];
  private dripTranslationsKey: string;
  private dripTranslationsCache: ModalPriceBreakdownTranslations;
}
